import { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Play, RotateCcw } from "lucide-react";
import TerminalMockup from "@/components/TerminalMockup";
import PhoneMockup from "@/components/PhoneMockup";
import { SCENARIOS } from "@/components/TerminalMockup";

const scenarioOptions = [
  { id: "approval_needed", label: "Approval Needed", desc: "Agent requests permission to install a package" },
  { id: "tests_failed", label: "Tests Failed", desc: "Test suite fails mid-session" },
  { id: "task_completed", label: "Task Completed", desc: "Migration finishes successfully" },
];

export default function DemoPage() {
  const [activeScenario, setActiveScenario] = useState("approval_needed");
  const [showNotification, setShowNotification] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [actionTaken, setActionTaken] = useState(null);
  const [isResumed, setIsResumed] = useState(false);
  const [demoKey, setDemoKey] = useState(0);

  useEffect(() => {
    document.title = "Runzr — Interactive Demo";
  }, []);

  const resetDemo = useCallback(() => {
    setShowNotification(false);
    setShowActions(false);
    setActionTaken(null);
    setIsResumed(false);
    setDemoKey((k) => k + 1);
  }, []);

  const handleScenarioChange = (id) => {
    setActiveScenario(id);
    resetDemo();
  };

  const handleNeedInput = useCallback(() => {
    setTimeout(() => {
      setShowNotification(true);
      setTimeout(() => setShowActions(true), 600);
    }, 400);
  }, []);

  const handleAction = useCallback((action) => {
    setActionTaken(action);
    setTimeout(() => setIsResumed(true), 800);
  }, []);

  const handleComplete = useCallback(() => {
    // demo complete
  }, []);

  const scenarioData = SCENARIOS[activeScenario];

  return (
    <div
      className="min-h-screen"
      style={{ background: "#0A0A0A" }}
      data-testid="demo-page"
    >
      {/* Header */}
      <div
        className="sticky top-0 z-50 border-b"
        style={{
          background: "rgba(10,10,10,0.9)",
          backdropFilter: "blur(20px)",
          borderColor: "rgba(255,255,255,0.08)",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              data-testid="demo-back-link"
              className="flex items-center gap-2 text-sm no-underline transition-colors"
              style={{ color: "#A1A1AA" }}
              onMouseEnter={(e) => (e.target.style.color = "#fff")}
              onMouseLeave={(e) => (e.target.style.color = "#A1A1AA")}
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Link>
            <div
              className="w-px h-6"
              style={{ background: "rgba(255,255,255,0.1)" }}
            />
            <span
              className="text-sm font-semibold text-white"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Interactive Demo
            </span>
          </div>
          <button
            onClick={resetDemo}
            data-testid="demo-restart-btn"
            className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg transition-all duration-200"
            style={{
              color: "#A1A1AA",
              background: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Restart
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Scenario selector */}
        <div className="mb-12">
          <h2
            className="text-2xl font-semibold text-white mb-2"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Choose a scenario
          </h2>
          <p className="text-sm mb-6" style={{ color: "#71717A" }}>
            Watch how Runzr handles different situations in real-time.
          </p>
          <div className="flex flex-wrap gap-3">
            {scenarioOptions.map((s) => (
              <button
                key={s.id}
                onClick={() => handleScenarioChange(s.id)}
                data-testid={`scenario-${s.id}-btn`}
                className="text-left px-4 py-3 rounded-xl transition-all duration-200"
                style={{
                  background:
                    activeScenario === s.id
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(255,255,255,0.02)",
                  border: `1px solid ${
                    activeScenario === s.id
                      ? "rgba(255,255,255,0.2)"
                      : "rgba(255,255,255,0.06)"
                  }`,
                }}
              >
                <div className="text-sm font-medium text-white">{s.label}</div>
                <div className="text-xs mt-1" style={{ color: "#71717A" }}>
                  {s.desc}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Demo area */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* Terminal - takes 3 columns */}
          <div className="lg:col-span-3">
            <motion.div
              key={demoKey}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <TerminalMockup
                scenario={activeScenario}
                onNeedInput={handleNeedInput}
                onComplete={handleComplete}
                isResumed={isResumed}
              />
            </motion.div>

            {/* Instructions */}
            <div
              className="mt-6 p-4 rounded-xl"
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <Play className="w-3.5 h-3.5" style={{ color: "#F59E0B" }} />
                <span
                  className="text-xs font-medium uppercase tracking-wider"
                  style={{
                    color: "#F59E0B",
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  How this works
                </span>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: "#A1A1AA" }}>
                {activeScenario === "approval_needed"
                  ? "Watch the terminal run a Claude Code session. When the agent needs to install a package, a notification appears on the phone. Tap 'Approve' to continue the session remotely."
                  : activeScenario === "tests_failed"
                  ? "The terminal runs a Codex CLI session that encounters test failures. The phone receives an alert. Choose 'Debug with AI' to have the agent auto-fix the issue."
                  : "Watch a full migration complete successfully. The phone receives a completion summary with a link to the pull request."}
              </p>
            </div>
          </div>

          {/* Phone - takes 2 columns */}
          <div className="lg:col-span-2 flex justify-center lg:sticky lg:top-24">
            <motion.div
              key={demoKey}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <PhoneMockup
                notification={scenarioData.notification}
                actions={scenarioData.actions}
                onAction={handleAction}
                showNotification={showNotification}
                showActions={showActions}
                actionTaken={actionTaken}
              />
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
